import { useCallback, Suspense, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Loadable } from "jotai/vanilla/utils/loadable";
import { Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { type Key, Typeahead } from "@zapier/design-system-beta";
import {
  agentAtomFamily_SUSPENDS,
  takeSnapshotSetter,
  agentsSearchAtomFamily,
  closeSecondaryToolbarMenuSetter,
} from "@zapier/canvas-state";

import useUpdateNode from "../../../hooks/useUpdateNode";
import { useEmitResourceConnectEvents } from "../../../hooks/useEmitResourceConnectEvents";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { ErrorBoundary } from "../../ErrorBoundary";
import { NodeToolbarBottom } from "../NodeToolbarBottom";
import { MenuWrapper } from "../MenuWrapper";
import { SelectedItem } from "../SelectedItem";

import styles from "./FloatingAgentsTypeahead.module.css";

type Props = {
  selectedAgentId: string | undefined;
};

function SelectedAgentLoader() {
  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <Typeahead
        aria-label="Search agents"
        isBusy={true}
        size="medium"
        defaultSelectedKey="loading"
      >
        <Typeahead.Item key="loading" id="loading">
          Loading agent...
        </Typeahead.Item>
      </Typeahead>
    </div>
  );
}

function SelectedAgentSuspended({ selectedAgentId }: Props) {
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();
  const selectedAgent = useAtomValue(agentAtomFamily_SUSPENDS(selectedAgentId));

  const onRemove = useCallback(() => {
    takeSnapshot();
    updateNodeAndSave({
      data: {
        agentId: undefined,
      },
    });
  }, [takeSnapshot, updateNodeAndSave]);

  return (
    <SelectedItem
      label={
        selectedAgent
          ? selectedAgent?.name || "Untitled Agent"
          : "Agent not found"
      }
      icon={<Icon name="navCentral" size={26} color="BrandOrange" />}
      clearSelection={onRemove}
    />
  );
}

function AgentsTypeahead() {
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();
  /* ------------------------------------------------------------------
   * Search handling
   * ------------------------------------------------------------------ */
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuSetter);

  // Fetch agents (non-suspending loadable)
  // Always fetch with current search term, including empty string for initial load
  const loadableAgents = useAtomValue(
    agentsSearchAtomFamily({
      searchTerm: debouncedSearchTerm,
    }),
  ) as Loadable<
    ReturnType<typeof agentsSearchAtomFamily> extends any ? any[] : never
  >;

  const isLoading = loadableAgents.state === "loading";
  const hasData = loadableAgents.state === "hasData";
  const agents = hasData ? loadableAgents.data : [];
  const { emitEventOnAgentConnect } = useEmitResourceConnectEvents();

  const handleSelectionChange = useCallback(
    (key: Key | null) => {
      takeSnapshot();
      updateNodeAndSave({
        data: {
          agentId: key,
        },
      });

      if (key) {
        emitEventOnAgentConnect(String(key));
      }

      closeSecondaryMenu();
    },
    [
      takeSnapshot,
      updateNodeAndSave,
      closeSecondaryMenu,
      emitEventOnAgentConnect,
    ],
  );

  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <Typeahead
        menuTrigger="focus"
        aria-label="Search agents"
        placeholder="Search agents"
        onInputChange={(value: string) => setSearchTerm(value)}
        onSelectionChange={handleSelectionChange}
        classNamePopOver={styles.popover}
        isBusy={isLoading}
        size="medium"
      >
        {agents.map((agent) => {
          return (
            <Typeahead.Item
              key={agent.id}
              id={agent.id}
              textValue={agent.name}
              className={styles.floatingTypeaheadItem}
              size="medium"
            >
              <Icon name="navCentral" size={22} color="BrandOrange" />
              <div className={styles.truncateText}>
                <Text type="SmallPrint2">{agent.name}</Text>
              </div>
            </Typeahead.Item>
          );
        })}
      </Typeahead>
    </div>
  );
}

function AgentsTypeaheadContent({ selectedAgentId }: Props) {
  if (selectedAgentId) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<SelectedAgentLoader />}>
          <SelectedAgentSuspended selectedAgentId={selectedAgentId} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // No selected agent – render search UI (does not suspend)
  return (
    <ErrorBoundary>
      <AgentsTypeahead />
    </ErrorBoundary>
  );
}

export function FloatingAgentsTypeahead({ selectedAgentId }: Props) {
  return (
    <NodeToolbarBottom>
      <MenuWrapper>
        <AgentsTypeaheadContent selectedAgentId={selectedAgentId} />
      </MenuWrapper>
    </NodeToolbarBottom>
  );
}
